import type { DependencyRule } from 'api/entityApi'
import type { CustomFieldDef } from 'types/entity'

export type CustomFieldValueMap = Record<number, string | string[] | boolean>

/**
 * Port of `GetEntityCustomFieldsData`'s renderer. Each datatype maps to a control
 * instead of a template string, and values live in React state rather than being
 * scraped back out of the DOM at save time.
 *
 * `dependencyMap` carries the parent→child choice rules; a Select that declares a
 * `ParentFieldId` shows only the choices its parent's current value allows.
 */
export function CustomFields({
  defs,
  values,
  onChange,
  dependencyMap,
}: {
  defs: CustomFieldDef[]
  values: CustomFieldValueMap
  onChange: (fieldId: number, value: string | string[] | boolean) => void
  dependencyMap?: Map<number, DependencyRule[]>
}) {
  if (defs.length === 0) {
    return <div className="empty-state">No custom fields configured.</div>
  }

  return (
    <div className="edit-grid">
      {defs.map((def) => (
        <div key={def.field_ID} className={def.datatype === 'Multiline' ? 'span-2' : undefined}>
          <div className="form-group">
            <label>
              {def.fieldname}
              {def.isMandatory && <span className="red">*</span>}
            </label>
            <CustomFieldControl
              def={def}
              value={values[def.field_ID]}
              onChange={onChange}
              choices={resolveChoices(def, values, dependencyMap)}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Choices for a field: its own list, unless it is a child field, in which case
 * the parent's currently selected value decides. Port of the dependency branch
 * in `GetEntityCustomFieldsData`.
 */
function resolveChoices(
  def: CustomFieldDef,
  values: CustomFieldValueMap,
  dependencyMap?: Map<number, DependencyRule[]>,
): string[] {
  const own = splitChoices(def.choices)

  const parentId = Number(def.ParentFieldId ?? 0)
  const rules = dependencyMap?.get(def.field_ID)
  if (!parentId || !rules?.length) return own

  // Fall back to the value the server recorded before the form was touched.
  const parentValue = asString(values[parentId]) || (def.ParentFieldValue ?? '')
  const rule = rules.find((r) => r.ParentValue === parentValue)

  return rule?.ChildValue?.length ? rule.ChildValue : own
}

function CustomFieldControl({
  def,
  value,
  onChange,
  choices,
}: {
  def: CustomFieldDef
  value: string | string[] | boolean | undefined
  onChange: (fieldId: number, value: string | string[] | boolean) => void
  choices: string[]
}) {
  const id = `CustField_${def.field_ID}`

  switch (def.datatype) {
    case 'Numeric':
      return (
        <input
          id={id}
          type="number"
          className="form-control"
          value={asString(value)}
          min={def.minvalue || undefined}
          max={def.maxvalue || undefined}
          placeholder={`${def.fieldname} (min ${def.minvalue}, max ${def.maxvalue})`}
          onChange={(e) => onChange(def.field_ID, e.target.value)}
        />
      )

    case 'Date':
      return (
        <input
          id={id}
          type="date"
          className="form-control"
          value={toDateInputValue(asString(value))}
          min={toDateInputValue(def.minvalue)}
          max={toDateInputValue(def.maxvalue)}
          onChange={(e) => onChange(def.field_ID, e.target.value)}
        />
      )

    case 'datetime':
      return (
        <input
          id={id}
          type="datetime-local"
          className="form-control"
          value={asString(value)}
          onChange={(e) => onChange(def.field_ID, e.target.value)}
        />
      )

    case 'time':
      return (
        <input
          id={id}
          type="time"
          className="form-control"
          value={asString(value)}
          onChange={(e) => onChange(def.field_ID, e.target.value)}
        />
      )

    case 'Multiline':
      return (
        <textarea
          id={id}
          rows={3}
          className="form-control"
          value={asString(value)}
          onChange={(e) => onChange(def.field_ID, e.target.value)}
        />
      )

    case 'Radio':
      return (
        <div>
          {choices.map((choice) => (
            <label className="radio-cont" key={choice}>
              <input
                type="radio"
                name={id}
                value={choice}
                checked={asString(value) === choice}
                onChange={() => onChange(def.field_ID, choice)}
              />
              {choice}
            </label>
          ))}
        </div>
      )

    case 'Checkbox':
      return (
        <label className="cont-checkbox">
          <input
            type="checkbox"
            id={id}
            checked={value === true || asString(value) === 'True'}
            onChange={(e) => onChange(def.field_ID, e.target.checked)}
          />
          {def.fieldname}
        </label>
      )

    case 'Select':
      return (
        <select
          id={id}
          className="form-control"
          value={asString(value)}
          onChange={(e) => onChange(def.field_ID, e.target.value)}
        >
          <option value="">-- select --</option>
          {choices.map((choice) => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </select>
      )

    case 'List': {
      const selected = Array.isArray(value) ? value : splitChoices(asString(value))
      return (
        <select
          id={id}
          multiple
          className="form-control"
          style={{ minHeight: 92 }}
          value={selected}
          onChange={(e) =>
            onChange(
              def.field_ID,
              Array.from(e.target.selectedOptions, (option) => option.value),
            )
          }
        >
          {choices.map((choice) => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </select>
      )
    }

    case 'Text':
    default:
      return (
        <input
          id={id}
          type="text"
          className="form-control"
          value={asString(value)}
          onChange={(e) => onChange(def.field_ID, e.target.value)}
        />
      )
  }
}

/** Seeds the value map from the definitions' current values. */
export function initialCustomFieldValues(defs: CustomFieldDef[]): CustomFieldValueMap {
  const map: CustomFieldValueMap = {}
  for (const def of defs) {
    const raw = def.fieldvalue ?? ''
    if (def.datatype === 'Checkbox') map[def.field_ID] = raw === 'True' || raw === 'true'
    else if (def.datatype === 'List') map[def.field_ID] = splitChoices(raw)
    else map[def.field_ID] = raw
  }
  return map
}

/** Shapes the value map into the `lstCustomField` array the save endpoints expect. */
export function toCustomFieldPayload(values: CustomFieldValueMap) {
  return Object.entries(values).map(([fieldId, value]) => ({
    FieldId: Number(fieldId),
    // FieldValue binds to a string server-side — a raw boolean would fail
    // deserialization, and the legacy page also posted "True"/"False".
    FieldValue: Array.isArray(value)
      ? value.join(',')
      : typeof value === 'boolean'
        ? value
          ? 'True'
          : 'False'
        : value,
  }))
}

function splitChoices(raw: string): string[] {
  if (!raw) return []
  return raw
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean)
}

function asString(value: string | string[] | boolean | undefined): string {
  if (value == null) return ''
  if (Array.isArray(value)) return value.join(',')
  if (typeof value === 'boolean') return value ? 'True' : 'False'
  return value
}

/** `<input type="date">` needs yyyy-MM-dd; stored values may be "30-Sep-2023". */
function toDateInputValue(raw: string): string {
  if (!raw) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return ''
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  return `${parsed.getFullYear()}-${month}-${day}`
}
